import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { clearSession } from '../APIService';
import { loginRequest, msalInstance, type SessionResult } from '../authConfig';
import { DEV_BYPASS_AUTH } from '../devFlags';

export type AuthStatus = 'signedIn' | 'signedOut' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  /** User-facing message when `status` is `'error'`. */
  error?: string;
  /** False when the app-access group gate is enabled and the account isn't a member. */
  hasAppAccessGroup: boolean;
  /** Starts the B2C redirect sign-in. Also used to retry after a failure. */
  login: () => void;
  /** Clears local state and ends the B2C session. */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  status: 'signedOut',
  hasAppAccessGroup: false,
  login: () => {},
  logout: () => {},
});

const signedOut = (): SessionResult => ({
  status: 'signedOut',
  hasAppAccessGroup: false,
});

/**
 * Owns the authentication lifecycle so no part of the app has to reload the page
 * to observe a sign-in.
 *
 * `initialSession` comes from the startup bootstrap in `main.tsx`, which has
 * already awaited MSAL's redirect handling and the token acquisition — so the
 * very first render already knows whether the user is signed in.
 *
 * There is no separate "retry without a full redirect" path any more. There
 * used to be one, re-running `establishSession()` in place, but with `App.tsx`
 * now redirecting to B2C automatically on `signedOut` — no button to click —
 * a bare re-check couldn't do anything a fresh `login()` doesn't already do,
 * and a stale, already-consumed redirect error had no way to clear itself
 * between attempts. `login()` is simpler and actually recovers.
 */
export const AuthProvider = ({
  initialSession,
  children,
}: {
  initialSession: SessionResult;
  children: ReactNode;
}) => {
  const [session, setSession] = useState<SessionResult>(initialSession);

  const login = useCallback(() => {
    setSession((current) => ({ ...current, error: undefined }));
    msalInstance.loginRedirect(loginRequest).catch((loginError) => {
      console.error('B2C login failed:', loginError);
      setSession({
        ...signedOut(),
        status: 'error',
        error: 'Could not start sign-in. Please try again.',
      });
    });
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setSession(signedOut());

    // Under the dev bypass there is no B2C account, so logoutRedirect would
    // throw. Clearing local state is the whole of sign-out in that mode.
    if (DEV_BYPASS_AUTH) {
      return;
    }

    // Ends the B2C session too. Clearing storage alone left the cached account
    // in place, so the next visit silently re-signed the same user back in.
    void msalInstance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session.status,
      error: session.error,
      hasAppAccessGroup: session.hasAppAccessGroup,
      login,
      logout,
    }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
